// Attribution: the shadow-portfolio deck's three tables for a month, from the analyst
// books' Refinitiv reports in the attribution DB, then each book's contributions by
// sub-asset class (with its funds beneath) for the month or since inception.
import React, { useState } from 'react';
import { API, fmtDmy, fmtMonth, fmtPct, NEG_FILL, POS_FILL, useApiGet } from './apUtils';
import { BookSelect, Notice, Toggle } from './apComponents';
import { AttributionTables, DeckGaps, ReturnsRiskTable } from './DeckTables';

const p2 = (v) => fmtPct(v, 2);
const fill = (v) => (v == null || Math.abs(v) < 0.005 ? undefined : { background: v > 0 ? POS_FILL : NEG_FILL });

function Contributions({ code, month, span }) {
  const { data, error, loading } = useApiGet(code && month ? `${API}/attribution/${code}/contributions` : null,
    { month, window: span });
  const [open, setOpen] = useState({});
  if (error) return <Notice kind="error">{error}</Notice>;
  if (loading || !data) return <div className="chart-loading">Loading contributions...</div>;
  return (
    <>
      <p className="ap-footnote">
        {data.report_type === 'SI' ? 'Since inception' : 'Month'}: {fmtDmy(data.period_start)} to {fmtDmy(data.period_end)}.
        Weights are average weights over the period. Contribution to return is Refinitiv&apos;s, by sub-asset class
        (Refinitiv does not report it per fund). Click a sub-asset class for its funds.
      </p>
      <div className="table-container">
        <table className="ap-contrib-table">
          <thead>
            <tr>
              <th className="ap-l">Sub-asset class / fund</th>
              <th>Weight</th><th>DS70 weight</th><th>Active</th>
              <th>Return</th><th>DS70 return</th>
              <th>Contribution</th><th>DS70 contribution</th>
              <th>Allocation</th><th>Selection</th><th>Total effect</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <React.Fragment key={r.sub_asset_class}>
                <tr className="ap-contrib-sac" onClick={() => setOpen((o) => ({ ...o, [r.sub_asset_class]: !o[r.sub_asset_class] }))}>
                  <td className="ap-l"><span className="expand-icon">{open[r.sub_asset_class] ? '▼' : '▶'}</span>{r.sub_asset_class}</td>
                  <td>{p2(r.weight)}</td><td>{p2(r.ds70_weight)}</td><td>{fmtPct(r.active_weight, 2, true)}</td>
                  <td>{p2(r.return)}</td><td>{p2(r.ds70_return)}</td>
                  <td>{p2(r.contribution)}</td><td>{p2(r.ds70_contribution)}</td>
                  <td style={fill(r.allocation)}>{p2(r.allocation)}</td>
                  <td style={fill(r.selection)}>{p2(r.selection)}</td>
                  <td style={fill(r.total)}>{p2(r.total)}</td>
                </tr>
                {open[r.sub_asset_class] && r.funds.map((f) => (
                  <tr key={`${r.sub_asset_class}-${f.name}`} className="ap-contrib-fund">
                    <td className="ap-l"><span className="holding-indent">└</span>{f.name}</td>
                    <td>{p2(f.weight)}</td><td>{p2(f.ds70_weight)}</td>
                    <td>{f.weight == null && f.ds70_weight == null ? '–' : fmtPct((f.weight || 0) - (f.ds70_weight || 0), 2, true)}</td>
                    <td>{p2(f.return)}</td><td>{p2(f.ds70_return)}</td>
                    <td>–</td><td>–</td><td>–</td>
                    <td style={fill(f.selection)}>{p2(f.selection)}</td>
                    <td style={fill(f.total)}>{p2(f.total)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr className="total-row">
              <td className="ap-l">Total</td>
              <td>100.00%</td><td>100.00%</td><td>–</td>
              <td>{p2(data.total.return)}</td><td>{p2(data.total.ds70_return)}</td>
              <td>{p2(data.total.contribution)}</td><td>{p2(data.total.ds70_contribution)}</td>
              <td style={fill(data.total.allocation)}>{p2(data.total.allocation)}</td>
              <td style={fill(data.total.selection)}>{p2(data.total.selection)}</td>
              <td style={fill(data.total.total)}>{p2(data.total.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function AttributionTab({ code, setCode, books }) {
  const months = useApiGet(`${API}/attribution/months`);
  const [picked, setPicked] = useState(null);
  const [span, setSpan] = useState('month');
  const list = months.data?.months || [];
  const month = picked && list.includes(picked) ? picked : list[0];
  const deck = useApiGet(month ? `${API}/attribution` : null, { month });

  if (months.error) return <div className="tab-panel"><Notice kind="error">{months.error}</Notice></div>;
  if (months.loading) return <div className="tab-panel"><div className="chart-loading">Loading attribution months...</div></div>;
  if (!list.length) {
    const missing = Object.entries(months.data?.si_missing || {});
    return (
      <div className="tab-panel">
        <Notice>
          No month has an SI report for every analyst book in the portal yet. The tables appear once the attribution DB,
          with the books&apos; monthly and SI reports, is synced to the cloud.
          {missing.length > 0 && ` SI reports missing: ${missing.map(([m, c]) => `${fmtMonth(m)} ${c.join(', ')}`).join('; ')}.`}
        </Notice>
      </div>
    );
  }
  return (
    <div className="tab-panel">
      <div className="chart-controls">
        <div className="chart-portfolio-selector">
          <label htmlFor="ap-attr-month">Month ending:</label>
          <select id="ap-attr-month" value={month} onChange={(e) => setPicked(e.target.value)}>
            {list.map((m) => <option key={m} value={m}>{fmtDmy(m)}</option>)}
          </select>
        </div>
      </div>
      {deck.error && <Notice kind="error">{deck.error}</Notice>}
      {deck.loading && <div className="chart-loading">Loading attribution...</div>}
      {deck.data && (
        <div className="ap-deck">
          <h4 className="ap-section-title">Shadow portfolio review — {fmtMonth(month)}</h4>
          <ReturnsRiskTable deck={deck.data} />
          <AttributionTables deck={deck.data} />
          <DeckGaps deck={deck.data} />
        </div>
      )}
      <h4 className="ap-section-title">Contributions by portfolio</h4>
      <BookSelect id="ap-attr-book" value={code} onChange={setCode} books={books}>
        <Toggle options={[['month', 'Month'], ['si', 'Since inception']]} value={span} onChange={setSpan} />
      </BookSelect>
      <Contributions code={code} month={month} span={span} />
    </div>
  );
}
